import { InfoIcon } from '@collinsonx/design-system/assets/icons';
import BookingLightbox from '@collinsonx/design-system/components/bookinglightbox';
import Heading from '@collinsonx/design-system/components/heading/Heading';
import { ActionIcon, Flex, Text } from '@collinsonx/design-system/core';
import { BlockWrapper } from '@components/BlockWrapper/BlockWrapper';
import LazyLoad from '@components/LazyLoad/LazyLoad';
import QuantityInput from '@components/QuantityInput';
import SvgAdapter from '@components/SvgAdapter/SvgAdapter';
import { getItem } from '@lib';
import { BOOKING_MODE } from 'config/constants';
import { BOOKING_MODE_STATE } from 'config/storage';
import { BookingContext } from 'context/BookingContext';
import useEntitlements from 'hooks/useEntitlements';
import useLocale from 'hooks/useLocale';
import { useContext, useEffect, useState } from 'react';
import colors from 'ui/colour-constants';
import { replaceTranslationPlaceholder } from 'utils/translations';

export default function EntitlementsBlock() {
  const [entitlementsModalOpen, setEntitlementsModalOpen] =
    useState<boolean>(false);

  const mode = getItem(BOOKING_MODE_STATE);
  const translations = useLocale();
  const { booking, setBooking } = useContext(BookingContext);
  const {
    entitlemenstExpiryDate,
    fetchingEntitlementsData,
    hasEntitlements,
    hasPermissionsForEntitlements,
    hasUnlimitedEntitlements,
    maxEntitlementsCount,
    remainingEntitlementsCount,
    usedEntitlementsCount,
  } = useEntitlements();

  const { selectedEntitlementsCount } = booking || {};

  const shouldHideComponent =
    (!fetchingEntitlementsData && !entitlemenstExpiryDate) ||
    !hasEntitlements ||
    !hasPermissionsForEntitlements;

  const loadingComponent = fetchingEntitlementsData && !entitlemenstExpiryDate;

  useEffect(() => {
    if (!hasUnlimitedEntitlements) return;

    handleSelectEntitlementsChange(maxEntitlementsCount);
  }, [maxEntitlementsCount, hasUnlimitedEntitlements]);

  useEffect(() => {
    if (maxEntitlementsCount >= selectedEntitlementsCount) return;
    if (selectedEntitlementsCount > 0) return;

    handleSelectEntitlementsChange(maxEntitlementsCount);
  }, [maxEntitlementsCount]);

  const handleSelectEntitlementsChange = (val: number) => {
    const entitlementsAreAmended =
      mode === BOOKING_MODE.EDIT && usedEntitlementsCount !== val;

    setBooking({
      entitlementsAreAmended,
      selectedEntitlementsCount: val,
      usedEntitlementsCount,
    });
  };

  return (
    <LazyLoad
      hideIf={shouldHideComponent}
      loading={loadingComponent}
      wrapper={BlockWrapper}
    >
      <BookingLightbox
        ctaCancel={translations.booking.entitlements.infoModal.btnClose}
        onClose={() => setEntitlementsModalOpen(false)}
        open={entitlementsModalOpen}
      >
        <Flex align="center" justify="center" p="10px 0" wrap="wrap">
          <Heading as="h2" lineHeight={1.2} margin={0} padding={0}>
            {translations.booking.entitlements.infoModal.title}
          </Heading>
          <Text m="10px 0 10px 0" size="xl" ta="center">
            {translations.booking.entitlements.infoModal.text}
          </Text>
        </Flex>
      </BookingLightbox>
      <Flex align="center" direction="row" gap="16px " justify="space-between">
        <Heading as="h2" margin={0} padding={0}>
          {translations.booking.entitlements.title}
        </Heading>
        <ActionIcon
          aria-label="Settings"
          onClick={() => setEntitlementsModalOpen(true)}
          role="button"
          style={{ marginTop: '0.3rem' }}
          variant="transparent"
        >
          <SvgAdapter fill={colors.theme}>
            <InfoIcon />
          </SvgAdapter>
        </ActionIcon>
      </Flex>
      <Text style={{ fontWeight: 'bold' }}>
        {!hasUnlimitedEntitlements
          ? replaceTranslationPlaceholder(
              translations.booking.entitlements.countAndExpiryDate,
              {
                count: remainingEntitlementsCount,
                date: entitlemenstExpiryDate,
              }
            )
          : replaceTranslationPlaceholder(
              translations.booking.entitlements.unlimitedEntitlements,
              { date: entitlemenstExpiryDate }
            )}
      </Text>
      <Text>{translations.booking.entitlements.inputLabel}</Text>
      <QuantityInput
        decrementLabel={translations.generic.labels.decrementEntitlements}
        incrementLabel={translations.generic.labels.incrementEntitlements}
        max={maxEntitlementsCount}
        onChange={(val) => handleSelectEntitlementsChange(val as number)}
        selectedLabel={translations.generic.labels.selectedEntitlements}
        value={selectedEntitlementsCount}
      />
    </LazyLoad>
  );
}
